/**
 * 사용자가 현재 보고 있는 페이지의 가벼운 컨텍스트를 수집해서 챗봇 백엔드로 전달.
 * 챗봇은 이 정보를 system prompt 의 보조 정보로 사용해 "지금 보고 있는 화면" 에 대한 답변 정확도를 올린다.
 *
 * 의도적으로 가볍게: URL/라우트, 페이지 제목, 가시 영역 H1/H2/주요 카드 라벨 정도만.
 * 민감 데이터(토큰/유저 PII) 는 절대 포함하지 않음.
 */
import { getInstanceByDom } from 'echarts/core';

export interface PageContext {
  url: string;
  path: string;
  title: string;
  /** 화면 안 헤딩(h1/h2)·alarm·dashboard 카드 라벨 같은 짧은 텍스트 fragments. */
  fragments: string[];
  /** 캡처 시각 (ISO). */
  capturedAt: string;
  /**
   * 이 메시지 직전에 챗봇이 본 path. chat store 가 매 send 직전에 갱신.
   * Backend system prompt 가 (current path != previous path) 인 경우 새 화면 진입으로 판단,
   * 사용자가 anaphora 없이 묻는다면 이전 turn 의 화면별 데이터를 인용하지 말도록 안내.
   */
  previousPath?: string;
  /**
   * 사용자가 화면에서 "활성화" 한 도메인 엔티티 (예: 슬라이드를 열어 둔 트랜잭션).
   * 화면별 setup 이 register/unregister 한 값을 그대로 직렬화. 없으면 undefined.
   * 권장 키 — entity_type/tid/was_id/start_time/end_time/collect_time/name/display_name 등 (모두 string).
   */
  activeEntity?: Record<string, string>;
  /**
   * 화면에서 보이는 실제 데이터 스냅샷 (filter 조건, 그리드 행, 차트 시리즈 등).
   * DOM 에서 가벼운 구조로 추출 — 챗봇이 "이 화면에 보이는 데이터" 분석 시 활용.
   *
   * 캡 (oversize 방지):
   *   - 그리드: 최대 3개, 각 그리드 헤더 전체 + 본문 첫 30행 × 셀 텍스트 100자
   *   - filter: 최대 25개 항목, 각 라벨 60자 + 값 80자
   *   - 직렬화 후 ~6KB 이하 목표
   */
  dataSnapshot?: DataSnapshot;
}

/** 화면 스냅샷 구조 (가벼운 plain JSON — 백엔드는 그대로 system prompt 에 inject). */
export interface DataSnapshot {
  filters?: Array<{ label: string; value: string }>;
  tables?: Array<{
    title?: string;
    columns: string[];
    /** 흥미로운 row 만 (top-K + min/max 보유 + last). 원본 grid 의 index 는 rowIndices 와 1:1. */
    rows: string[][];
    /** 실제 화면에 렌더된 (또는 가상화된) 총 row 수 — rows[].length 와 다를 수 있음. */
    visibleRowCount?: number;
    /** sampled rows[] 의 원본 grid 안 row 번호. 사용자에게 정확한 row index 인용 가능. */
    rowIndices?: number[];
    /** 컬럼별 분류 + 통계. LLM 이 '이 컬럼 max / 평균' 같은 질문에 즉답 가능. */
    columnStats?: ColumnStat[];
  }>;
  /**
   * Dashboard widget 의 raw 데이터. ECharts / Score / Gauge / Hexagon 4종 지원.
   * 위젯 종류별 페이로드:
   *   - echart : series[] (시계열/카테고리 데이터, thinning 적용)
   *   - score  : values[] (큰 숫자 + 라벨 — ScoreCard / Scoreboard / StatusSummaryCard)
   *   - gauge  : gauges[] (CircleGaugeChart 의 value/max/unit)
   *   - hexagon: hexagons[] (HexagonChart 의 cell/level)
   *   - unknown: title + analyzable=false (TopologyGraph 등 그래픽 위주)
   */
  widgets?: WidgetSnapshot[];
}

/**
 * Grid/table 컬럼 분류 + 통계.
 *
 * - numeric: 90% 이상 셀이 숫자로 파싱됨 (천 단위 콤마 / 단위 suffix 제거 후).
 * - categorical: 고유값 < 셀의 10% 이면서 distinct <= 20.
 * - id: 헤더명이 *id / *Id / *ID / *_id / pid / sid / tid 류 + 고유값 80% 이상.
 * - text: 위 어느 것도 아닌 자유 문자열.
 */
export type ColumnType = 'numeric' | 'categorical' | 'text' | 'id';

export interface ColumnStat {
  name: string;
  type: ColumnType;
  /** null/blank/parse-fail count */
  nullCount?: number;
  /** numeric */
  min?: number;
  max?: number;
  avg?: number;
  p50?: number;
  p95?: number;
  /** numeric — sampled rows 안에서 min/max 보유 row 의 sampled index (0-based). */
  minRowIdx?: number;
  maxRowIdx?: number;
  /** categorical */
  distinct?: number;
  topValues?: Array<[string, number]>;
  /** text */
  avgLen?: number;
  maxLen?: number;
}

/**
 * 시계열 series 의 통계 요약. raw points 를 inject 하지 않고 LLM 추론에 충분한
 * 통계량 + 시간범위 + 추세 메타만 보낸다.
 */
export interface SeriesSummary {
  /** 통계 — 모두 number | null. NaN/Infinity 제외 후 계산. */
  max: number | null;
  min: number | null;
  first: number | null;
  last: number | null;
  avg: number | null;
  /** 표준편차 (population). 변동성 척도 — 평균과 함께 보면 안정성 판정. */
  stddev: number | null;
  /** 95th percentile — long-tail anomaly. avg 에 가려진 spike 검출. */
  p95: number | null;
  /** 유효 (non-null, finite) 포인트 수. */
  pointCount: number;
  /** null / NaN / Infinity 인 포인트 수 (수집 gap 검출). */
  nullCount: number;
  /** pointCount + nullCount = 원본 포인트 수. */
  totalCount: number;
  /** first→last 추세 방향. Δ% < 3 → flat / >= 3 → up / <= -3 → down. */
  direction: 'up' | 'down' | 'flat' | 'unknown';
  /** 첫 / 마지막 label 의 ISO timestamp (label 이 시간형일 때만). */
  from?: string;
  to?: string;
  /** 전체 범위 길이 (ms). to - from. */
  durationMs?: number;
  /** 연속 label 간의 median 간격 (ms). 1만 있거나 시간형 아닐 때 undefined. */
  intervalMs?: number;
  /** max 가 발생한 label 의 ISO timestamp. */
  maxAt?: string;
  /** min 이 발생한 label 의 ISO timestamp. */
  minAt?: string;
}

export interface WidgetSnapshot {
  widget_id?: string;
  title?: string;
  kind:
    | 'echart'
    | 'score'
    | 'gauge'
    | 'hexagon'
    | 'server'
    | 'overview'
    | 'top_list'
    | 'text'
    | 'unknown';
  /** BaseWidgetFrame 의 chartType (TIME_SERIES / STATUS_SUMMARY / SERVER / EXCLUSIVE / ...). */
  chartType?: string;
  /** widget 이 사용하는 metric dataId 목록 (chartData[].dataId 에서 수집). backend 가 description 조회 시 활용. */
  dataIds?: string[];
  /** widget 이 데이터 로드 못 함 (isNoData). LLM 이 "이 metric 은 설정되어 있으나 현재 값 없음" 으로 인용. */
  noData?: boolean;
  /**
   * 시계열 차트 series. raw data points 대신 summary stats + metric define 동봉.
   * dataId / displayName / unit / description 등은 widget component 의
   * `chartRawData[*].dataDefinition` 에서 추출 (가능한 경우만).
   */
  series?: Array<{
    name?: string;
    type?: string;
    dataId?: string;
    displayName?: string;
    unit?: string;
    description?: string;
    pcategory?: string;
    ccategory?: string;
    summary: SeriesSummary;
  }>;
  values?: Array<{ label?: string; value: string }>;
  gauges?: Array<{
    id?: string;
    value: number | null;
    max?: number;
    unit?: string;
  }>;
  hexagons?: Array<{
    key?: string;
    value: number | null;
    level?: string;
  }>;
  /**
   * Widget 의 현재 알람 상태. 사용자가 BaseWidgetFrame 의 widgetAlertInfo 로 활성화한
   * 알람 표시 (위젯 테두리 빨강/노랑) 가 있을 때 동봉. 활성 시 LLM 이 우선 분석.
   */
  alert?: {
    /** 알람이 현재 활성화되어 있는지 (isAlertHighlight = true). */
    active: boolean;
    /** 알람 레벨 — 'critical' | 'warning' 등. inactive 면 undefined. */
    level?: string;
    /** 알람 색상 (hex). */
    color?: string;
    /** 알람 트리거 metric field 명 (있으면). */
    fieldName?: string;
  };
  analyzable: boolean;
  truncated?: boolean;
  hint?: string;
}

/**
 * 화면별 setup 에서 호출하여 현재 슬라이드/패널의 활성 엔티티를 등록.
 * 같은 entity_type 으로 다시 호출 시 덮어씀. clear 는 unregisterChatbotActiveEntity().
 */
declare global {
  interface Window {
    __chatbotActiveEntity?: Record<string, string>;
    /**
     * 챗봇이 analyze_in_screen 도구로 화면 prefill 을 요청한 경우 본 객체에 저장.
     * 화면별 setup() 이 onMounted 에서 consume 하여 즉시 적용 후 clear.
     * 키:
     *   path        : prefill 이 적용될 라우트 (mismatched 시 화면이 무시)
     *   instance_id : 자동 선택할 인스턴스 UUID
     *   instance_name : 표시명 (옵션)
     *   from_time / to_time : ISO-8601 UTC
     *   auto_search : true 면 화면이 즉시 onRetrieve 호출
     *   intent / sub_kind : LLM 디버그 / 화면별 분기 hint
     */
    __chatbotScreenPrefill?: ChatbotScreenPrefill;
  }
}

export interface ChatbotScreenPrefill {
  path: string;
  instance_id?: string;
  instance_name?: string;
  from_time?: string;
  to_time?: string;
  auto_search?: boolean;
  intent?: string;
  sub_kind?: string;
  /**
   * 알람 임계치 추천 '알람 추가' 전용 — /alert/userAlert 화면이 소비하여 알람 규칙 생성 창을
   * 추천값으로 prefill 한다. 대상(인스턴스)은 사용자가 직접 선택한다.
   */
  alarm?: {
    category: string;
    targetCategory?: string;
    dataId: string;
    metricName: string;
    targetId: string;
    targetName: string;
    warning: number | null;
    critical: number | null;
    warningComparison: string;
    criticalComparison: string;
  };
}

/** 챗봇이 navigate 이벤트로 받은 prefill 등록. 같은 path 면 덮어씀. */
export const registerChatbotScreenPrefill = (prefill: ChatbotScreenPrefill): void => {
  if (typeof window === 'undefined') return;
  window.__chatbotScreenPrefill = prefill;
};

/**
 * 화면 setup 이 호출하여 prefill 을 1회 소비. path 가 일치하지 않으면 null 반환.
 * 소비 후 자동 clear (재진입 시 재실행되지 않게).
 */
export const consumeChatbotScreenPrefill = (expectedPath: string): ChatbotScreenPrefill | null => {
  if (typeof window === 'undefined') return null;
  const p = window.__chatbotScreenPrefill;
  if (!p) return null;
  if (p.path !== expectedPath) return null;
  window.__chatbotScreenPrefill = undefined;
  return p;
};

export const registerChatbotActiveEntity = (entity: Record<string, string>): void => {
  if (typeof window === 'undefined') return;
  // 모든 값을 String 으로 강제 (chatbot DTO 가 Map<String,String>).
  const normalized: Record<string, string> = {};
  for (const [k, v] of Object.entries(entity)) {
    if (v == null) continue;
    normalized[k] = String(v);
  }
  window.__chatbotActiveEntity = normalized;
};

export const unregisterChatbotActiveEntity = (entityType?: string): void => {
  if (typeof window === 'undefined') return;
  if (!entityType || window.__chatbotActiveEntity?.entity_type === entityType) {
    window.__chatbotActiveEntity = undefined;
  }
};

const MAX_FRAGMENT_LEN = 80;
const MAX_FRAGMENTS = 25;

// dataSnapshot 캡 — system prompt 폭발 방지
const SNAPSHOT_MAX_TABLES = 3;
/** DOM 에서 1차 스캔할 최대 row. 이 중 SNAPSHOT_MAX_SAMPLED_ROWS 만 LLM 으로 송신. */
const SNAPSHOT_MAX_ROWS_SCAN = 50;
/** LLM 으로 송신할 흥미로운 row 의 최대 수 (top + min/max bearing + last). */
const SNAPSHOT_MAX_SAMPLED_ROWS = 10;
/** 셀 길이 cap — 컬럼 type 별 차등. numeric/id 는 짧게, text 만 길게 허용. */
const SNAPSHOT_CELL_LEN_NUMERIC = 24;
const SNAPSHOT_CELL_LEN_CATEGORICAL = 40;
const SNAPSHOT_CELL_LEN_ID = 32;
const SNAPSHOT_CELL_LEN_TEXT = 100;
/** Categorical topValues 노출 최대 개수. */
const SNAPSHOT_CATEGORICAL_TOP_N = 5;
/** Categorical 판정 — 고유값 비율 임계 + 절대 distinct 상한. */
const SNAPSHOT_CATEGORICAL_DISTINCT_MAX = 20;
const SNAPSHOT_CATEGORICAL_RATIO_MAX = 0.1;
/** Numeric 판정 — 셀의 parseable 비율 임계. */
const SNAPSHOT_NUMERIC_PARSE_RATIO = 0.9;
/** ID 컬럼 헤더 휴리스틱 — 정규식 match 시 type=id 우선. */
const ID_HEADER_PATTERN = /(^|[_\s-])(id|pid|sid|tid|uuid|seq|no|num|ord(er)?|idx|index)$/i;
const SNAPSHOT_MAX_FILTERS = 25;
const SNAPSHOT_MAX_FILTER_VALUE_LEN = 80;
const SNAPSHOT_MAX_FILTER_LABEL_LEN = 60;

// Widget snapshot 캡
const WIDGET_MAX_COUNT = 30;
const WIDGET_MAX_SERIES_PER_CHART = 8;
const WIDGET_MAX_VALUES_PER_WIDGET = 200;
const WIDGET_MAX_HEXAGON_CELLS = 200;
const WIDGET_MAX_TITLE_LEN = 80;
const WIDGET_MAX_VALUE_LEN = 60;
const WIDGET_MAX_LABEL_LEN = 40;
/** dataSnapshot 전체 JSON.stringify size cap. 초과 시 widgets tail 부터 drop. */
const SNAPSHOT_MAX_BYTES = 48 * 1024;

// 화면 텍스트가 매 발화마다 백엔드 → (설정된 경우) 외부 LLM 까지 전달되므로, 수집 단계에서
// PII 패턴을 마스킹한다 (MR !13408 리뷰 P1-3). cleanText 는 필터/그리드 셀/본문 fragment 등
// 이 모듈의 모든 텍스트 수집이 거치는 단일 관문이라 여기서 일괄 적용한다.
const EMAIL_RE = /[\w.+-]+@[\w-]+\.[\w.-]{2,}/g;
// 전화번호형: 구분자(-, ., 공백)로 나뉜 2~4 / 3~4 / 4 자리 — IP(옥텟 최대 3자리·4그룹)·
// 타임스탬프(2그룹)·포트(단일)와는 패턴이 달라 모니터링 수치 오마스킹 위험이 낮다.
const PHONE_RE = /\b\d{2,4}[-.\s]\d{3,4}[-.\s]\d{4}\b/g;
const redactPii = (s: string): string => s.replace(EMAIL_RE, '[email]').replace(PHONE_RE, '[tel]');

const cleanText = (s: string | null | undefined): string =>
  redactPii((s || '').replace(/\s+/g, ' ').trim());

const truncate = (s: string, max: number): string =>
  s.length > max ? `${s.substring(0, max)}…` : s;

/** 민감해 보이는 input 인지 (password / token / secret / 연락처·계정 계열 / hidden). 대소문자 무시. */
const isSensitiveInput = (el: Element): boolean => {
  const type = (el.getAttribute('type') || '').toLowerCase();
  // hidden 은 화면에 보이지도 않는 값(토큰/내부 상태)이라 수집 대상이 아니다 (재리뷰 P1)
  if (type === 'password' || type === 'email' || type === 'tel' || type === 'hidden') return true;
  const name = `${el.getAttribute('name') || el.getAttribute('id') || ''}`.toLowerCase();
  return /password|secret|token|credit|ssn|credential|email|phone|mobile|username|login/.test(name);
};

/**
 * 라벨/컬럼명 기준 민감 판정 — cleanText 의 값 패턴 마스킹(이메일/전화)은 일반 문자열
 * (사용자명, 계정, 토큰값 등)을 못 잡으므로, 라벨이 민감하면 값 자체를 수집/마스킹 차단한다.
 */
const SENSITIVE_LABEL_RE =
  /password|비밀번호|secret|token|credential|이메일|e-?mail|전화|연락처|phone|mobile|휴대|사용자\s?(id|명|이름)|user\s?(id|name)|계정|account|login|아이디/i;

/**
 * 가시 영역에서 활성 filter 들을 추출.
 * 대상: PA 필터바(.pa-filter), widget label item, 일반 input/select 중 라벨이 있는 것.
 */
const collectFilters = (): Array<{ label: string; value: string }> => {
  const out: Array<{ label: string; value: string }> = [];
  const seen = new Set<string>();
  const pushPair = (rawLabel: string | null | undefined, rawValue: string | null | undefined) => {
    const label = truncate(cleanText(rawLabel), SNAPSHOT_MAX_FILTER_LABEL_LEN);
    const value = truncate(cleanText(rawValue), SNAPSHOT_MAX_FILTER_VALUE_LEN);
    if (!label || !value) return;
    // widget-label-item 경로 포함 모든 필터 수집의 단일 관문 — 민감 라벨 값은 수집하지 않는다 (재리뷰 P1)
    if (SENSITIVE_LABEL_RE.test(label)) return;
    const key = `${label}\0${value}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ label, value });
  };

  // 1) widget-label-item 패턴 (exemONE 공용 PA filter 컴포넌트)
  document.querySelectorAll('.widget-label-item, [class*="widget-label-item"]').forEach((wrap) => {
    const labelEl = wrap.querySelector('label, .label, [class*="__label"]');
    const trigger = wrap.querySelector(
      '.wide-dropdown-select__select-value, input, select, [class*="__value"]',
    );
    if (!labelEl || !trigger) return;
    const valTxt = (trigger as HTMLInputElement).value ?? trigger.textContent ?? '';
    pushPair(labelEl.textContent, valTxt);
  });

  // 2) 일반 input/select — 직전 sibling label 또는 placeholder 기반
  document.querySelectorAll('input, select').forEach((el) => {
    if (isSensitiveInput(el)) return;
    const v = (el as HTMLInputElement).value;
    if (!v) return;
    // label 추론
    let label = '';
    const id = el.getAttribute('id');
    if (id) {
      const labelEl = document.querySelector(`label[for="${CSS.escape(id)}"]`);
      if (labelEl) label = cleanText(labelEl.textContent);
    }
    if (!label) {
      const ph = el.getAttribute('placeholder');
      if (ph) label = cleanText(ph);
    }
    if (!label) {
      const aria = el.getAttribute('aria-label');
      if (aria) label = cleanText(aria);
    }
    if (!label) return; // 라벨 없는 input 은 noise
    pushPair(label, v);
  });

  return out.slice(0, SNAPSHOT_MAX_FILTERS);
};

/**
 * ModernGrid 패턴(.modern-grid-header 와 .modern-grid-body) 의 헤더 + 본문 텍스트 추출.
 * 가시 영역에 렌더된 셀만 보이지만 가상화된 row 도 일부 포함될 수 있음 — 첫 N개만 채택.
 *
 * 한 화면에 그리드가 여러 개일 수 있어 최대 3개까지.
 */
/**
 * 셀 문자열을 숫자로 파싱 시도. 천 단위 콤마, 단위 suffix (ms / % / KB / s) 제거 후 parseFloat.
 * 실패 시 NaN. 비어 있으면 NaN.
 */
const tryParseNumeric = (raw: string): number => {
  if (!raw) return NaN;
  // "1,234.5 ms" / "45.2%" / "(empty)" / "—" 같은 패턴 처리
  const cleaned = raw
    .replace(/[,\s]/g, '')
    .replace(/(?:%|ms|s|sec|min|hr|h|kb|mb|gb|tb|b|건|회|개|명|초|분|시간|회|건수|개)$/i, '');
  if (!cleaned || cleaned === '-' || cleaned === '—' || /^[()[\]]+$/.test(cleaned)) return NaN;
  const v = parseFloat(cleaned);
  return Number.isFinite(v) ? v : NaN;
};

const isBlankCell = (s: string): boolean =>
  !s || s === '-' || s === '—' || s === '(empty)' || s === 'null' || s === 'N/A';

/** 컬럼 타입 자동 분류. column name + values 양쪽 휴리스틱. */
const classifyColumn = (name: string, values: string[]): ColumnType => {
  const total = values.length;
  if (total === 0) return 'text';
  const nonBlank = values.filter((v) => !isBlankCell(v));
  if (nonBlank.length === 0) return 'text';

  const numericCount = nonBlank.filter((v) => !Number.isNaN(tryParseNumeric(v))).length;
  const numericRatio = numericCount / nonBlank.length;

  const distinct = new Set(nonBlank).size;
  const distinctRatio = distinct / nonBlank.length;

  // ID 우선 — 헤더명 패턴 + 고유값 비율 ≥ 80%
  if (ID_HEADER_PATTERN.test(name) && distinctRatio >= 0.8) return 'id';

  if (numericRatio >= SNAPSHOT_NUMERIC_PARSE_RATIO) return 'numeric';
  if (
    distinct <= SNAPSHOT_CATEGORICAL_DISTINCT_MAX &&
    distinctRatio < SNAPSHOT_CATEGORICAL_RATIO_MAX
  ) {
    return 'categorical';
  }
  return 'text';
};

/** Numeric column 통계 — sampled rows 의 idx 까지 계산 (LLM 이 원본 row 인용 가능). */
const computeNumericStat = (name: string, values: string[]): ColumnStat => {
  const pairs: Array<{ v: number; idx: number }> = [];
  let nullCount = 0;
  values.forEach((raw, idx) => {
    if (isBlankCell(raw)) {
      nullCount++;
      return;
    }
    const v = tryParseNumeric(raw);
    if (Number.isNaN(v)) {
      nullCount++;
      return;
    }
    pairs.push({ v, idx });
  });
  if (pairs.length === 0) {
    return { name, type: 'numeric', nullCount };
  }
  const sorted = [...pairs].sort((a, b) => a.v - b.v);
  const min = sorted[0];
  const max = sorted[sorted.length - 1];
  const sum = pairs.reduce((s, p) => s + p.v, 0);
  const avg = sum / pairs.length;
  const p50 = sorted[Math.floor(sorted.length * 0.5)].v;
  const p95 = sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.95))].v;
  const round = (x: number) => Math.round(x * 100) / 100;
  return {
    name,
    type: 'numeric',
    nullCount,
    min: round(min.v),
    max: round(max.v),
    avg: round(avg),
    p50: round(p50),
    p95: round(p95),
    minRowIdx: min.idx,
    maxRowIdx: max.idx,
  };
};

const computeCategoricalStat = (name: string, values: string[]): ColumnStat => {
  const freq = new Map<string, number>();
  let nullCount = 0;
  for (const v of values) {
    if (isBlankCell(v)) {
      nullCount++;
      continue;
    }
    freq.set(v, (freq.get(v) || 0) + 1);
  }
  const top: Array<[string, number]> = Array.from(freq.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, SNAPSHOT_CATEGORICAL_TOP_N);
  return {
    name,
    type: 'categorical',
    distinct: freq.size,
    topValues: top,
    nullCount,
  };
};

const computeTextStat = (name: string, values: string[]): ColumnStat => {
  let nullCount = 0;
  let sumLen = 0;
  let maxLen = 0;
  let count = 0;
  for (const v of values) {
    if (isBlankCell(v)) {
      nullCount++;
      continue;
    }
    count++;
    sumLen += v.length;
    if (v.length > maxLen) maxLen = v.length;
  }
  return {
    name,
    type: 'text',
    nullCount,
    avgLen: count > 0 ? Math.round(sumLen / count) : 0,
    maxLen,
  };
};

/**
 * 흥미로운 row index 선정:
 *   1. 사용자가 정렬해둔 화면의 top-5 (DOM order 의 첫 5)
 *   2. 마지막 row (bottom)
 *   3. 각 numeric 컬럼의 min/max 보유 row idx
 * 중복 dedupe + cap 적용 후 원본 순서 유지하여 반환.
 */
const pickInterestingRows = (rowCount: number, stats: ColumnStat[], cap: number): number[] => {
  if (rowCount === 0) return [];
  const picks = new Set<number>();
  for (let i = 0; i < Math.min(5, rowCount); i++) picks.add(i);
  picks.add(rowCount - 1);
  for (const s of stats) {
    if (s.type === 'numeric') {
      if (typeof s.minRowIdx === 'number') picks.add(s.minRowIdx);
      if (typeof s.maxRowIdx === 'number') picks.add(s.maxRowIdx);
    }
  }
  return Array.from(picks)
    .sort((a, b) => a - b)
    .slice(0, cap);
};

/** 컬럼 type 기준 셀 cap. */
const cellCapForType = (type: ColumnType): number => {
  switch (type) {
    case 'numeric':
      return SNAPSHOT_CELL_LEN_NUMERIC;
    case 'categorical':
      return SNAPSHOT_CELL_LEN_CATEGORICAL;
    case 'id':
      return SNAPSHOT_CELL_LEN_ID;
    case 'text':
    default:
      return SNAPSHOT_CELL_LEN_TEXT;
  }
};

const collectTables = (): DataSnapshot['tables'] => {
  const grids = Array.from(document.querySelectorAll('.modern-grid')).slice(
    0,
    SNAPSHOT_MAX_TABLES,
  ) as HTMLElement[];
  if (grids.length === 0) return undefined;

  const out: NonNullable<DataSnapshot['tables']> = [];
  for (const grid of grids) {
    // title 추출 — 그리드 직전의 가까운 헤더 / 패널 타이틀
    let title = '';
    let cur: Element | null = grid;
    for (let i = 0; i < 5 && cur && !title; i++) {
      const titleEl = cur.parentElement?.querySelector(
        ':scope > [class*="__title"], :scope > h1, :scope > h2, :scope > h3',
      );
      if (titleEl) title = cleanText(titleEl.textContent);
      cur = cur.parentElement;
    }

    // 헤더 셀 (cap 잠시 풍부하게 — type 분류 후 다시 truncate)
    const headerCells = grid.querySelectorAll(
      '.modern-grid-header .modern-grid-header__cell .modern-grid-header__cell-value, ' +
        '.modern-grid-header .modern-grid-header__cell-value',
    );
    const rawColumns: string[] = [];
    headerCells.forEach((c) => {
      const txt = truncate(cleanText(c.textContent), SNAPSHOT_CELL_LEN_TEXT);
      if (txt) rawColumns.push(txt);
    });
    if (rawColumns.length === 0) continue;

    // 본문 row — body__row 단위로 셀 추출 (1차 스캔: SNAPSHOT_MAX_ROWS_SCAN 까지)
    const bodyRows = Array.from(grid.querySelectorAll('.modern-grid-body__row')).slice(
      0,
      SNAPSHOT_MAX_ROWS_SCAN,
    );
    const rawRows: string[][] = [];
    for (const tr of bodyRows) {
      const cells = tr.querySelectorAll(
        '.modern-grid-body__cell .modern-grid-body__cell-value-wrapper, ' +
          '.modern-grid-body__cell .modern-grid-body__cell-value, ' +
          '.modern-grid-body__cell',
      );
      // querySelectorAll above can match nested elements duplicated — dedupe by closest cell
      const seenCells = new Set<Element>();
      const rowCells: string[] = [];
      cells.forEach((c) => {
        const cellRoot = c.closest('.modern-grid-body__cell');
        if (!cellRoot || seenCells.has(cellRoot)) return;
        seenCells.add(cellRoot);
        rowCells.push(cleanText(cellRoot.textContent));
      });
      if (rowCells.length > 0) rawRows.push(rowCells);
    }

    // 사용자 식별 컬럼(사용자명/User ID/이메일/계정 등)은 값 자체를 마스킹 — cleanText 의
    // 패턴 마스킹만으로는 일반 문자열 사용자명이 그대로 남는다 (재리뷰 P1)
    const sensitiveCol = rawColumns.map((c) => SENSITIVE_LABEL_RE.test(c));
    if (sensitiveCol.some(Boolean)) {
      for (const row of rawRows) {
        for (let i = 0; i < row.length; i += 1) {
          if (sensitiveCol[i] && row[i]) row[i] = '[masked]';
        }
      }
    }

    if (rawRows.length === 0) {
      // 데이터 없는 빈 grid — 컬럼만 보내도 의미 없음. 빈 표시로만 push.
      out.push({
        title: title || undefined,
        columns: rawColumns,
        rows: [],
        visibleRowCount: 0,
      });
      continue;
    }

    // === 빈 컬럼 제거 ===
    // 모든 row 셀이 blank 인 컬럼 인덱스 식별 → columns + 각 row 에서 동시 drop.
    const colCount = rawColumns.length;
    const keepCol: boolean[] = new Array(colCount).fill(false);
    for (let ci = 0; ci < colCount; ci++) {
      for (let ri = 0; ri < rawRows.length; ri++) {
        const cell = rawRows[ri][ci] || '';
        if (!isBlankCell(cell)) {
          keepCol[ci] = true;
          break;
        }
      }
    }
    const keptIndices: number[] = [];
    keepCol.forEach((keep, i) => {
      if (keep) keptIndices.push(i);
    });
    const columns = keptIndices.map((i) => rawColumns[i]);
    const compactedRows = rawRows.map((row) => keptIndices.map((i) => row[i] || ''));

    // === 컬럼 type 분류 + 통계 ===
    const columnStats: ColumnStat[] = columns.map((name, ci) => {
      const colValues = compactedRows.map((r) => r[ci] || '');
      const type = classifyColumn(name, colValues);
      switch (type) {
        case 'numeric':
          return computeNumericStat(name, colValues);
        case 'categorical':
          return computeCategoricalStat(name, colValues);
        case 'id':
          // ID 컬럼은 통계 의미 없음 — nullCount + type 만.
          return {
            name,
            type: 'id',
            nullCount: colValues.filter(isBlankCell).length,
          };
        case 'text':
        default:
          return computeTextStat(name, colValues);
      }
    });

    // === 흥미로운 row 선정 + 셀 cap 차등 적용 ===
    const sampledIndices = pickInterestingRows(
      compactedRows.length,
      columnStats,
      SNAPSHOT_MAX_SAMPLED_ROWS,
    );
    const sampledRows: string[][] = sampledIndices.map((origIdx) =>
      compactedRows[origIdx].map((cell, ci) =>
        truncate(cell, cellCapForType(columnStats[ci].type)),
      ),
    );

    out.push({
      title: title || undefined,
      columns,
      rows: sampledRows,
      visibleRowCount: rawRows.length, // DOM 1차 스캔 row 수 (사용자가 가상 스크롤로 더 가질 수 있음)
      rowIndices: sampledIndices,
      columnStats,
    });
  }
  return out.length > 0 ? out : undefined;
};

/**
 * label 을 ms timestamp 로 정규화. dayjs object / ISO string / number 모두 수용.
 * 시간형 아닌 label (categorical 문자열 등) 은 NaN 반환.
 */
const labelToMs = (l: unknown): number => {
  if (l && typeof l === 'object') {
    const obj = l as { valueOf?: () => unknown };
    if (typeof obj.valueOf === 'function') {
      const v = obj.valueOf();
      if (typeof v === 'number' && Number.isFinite(v)) return v;
    }
    return NaN;
  }
  if (typeof l === 'number') return Number.isFinite(l) ? l : NaN;
  if (typeof l === 'string') {
    const ms = Date.parse(l);
    return Number.isNaN(ms) ? NaN : ms;
  }
  return NaN;
};

/**
 * 시계열 series 의 통계 + 시간범위 + 추세 요약. raw points 대신 LLM 추론에 충분한
 * 양 (max/min/first/last/avg/stddev/p95 + maxAt/minAt + direction + nullCount + duration)
 * 만 추출. NaN/null 은 nullCount 로 카운트.
 */
const summarizeNumericSeries = (values: unknown[], labels: unknown[]): SeriesSummary => {
  let max = -Infinity;
  let min = Infinity;
  let maxIdx = -1;
  let minIdx = -1;
  let sum = 0;
  let count = 0;
  let nullCount = 0;
  let first: number | null = null;
  let last: number | null = null;
  const validValues: number[] = [];
  for (let i = 0; i < values.length; i += 1) {
    const v = values[i];
    const n = typeof v === 'number' ? v : v == null ? NaN : Number(v);
    if (!Number.isFinite(n)) {
      nullCount += 1;
      continue;
    }
    if (first === null) first = n;
    if (n > max) {
      max = n;
      maxIdx = i;
    }
    if (n < min) {
      min = n;
      minIdx = i;
    }
    sum += n;
    count += 1;
    last = n;
    validValues.push(n);
  }
  // stddev (population)
  let stddev: number | null = null;
  if (count > 0) {
    const mean = sum / count;
    let sqSum = 0;
    for (const v of validValues) sqSum += (v - mean) ** 2;
    stddev = Math.sqrt(sqSum / count);
  }
  // p95
  let p95: number | null = null;
  if (count > 0) {
    const sorted = [...validValues].sort((a, b) => a - b);
    const rank = Math.min(sorted.length - 1, Math.floor(0.95 * (sorted.length - 1)));
    p95 = sorted[rank];
  }

  // labels → ms 정규화 (값 위치 보존 — maxAt/minAt 는 index 매핑)
  const labelMs: number[] = [];
  for (let i = 0; i < labels.length; i += 1) {
    labelMs[i] = labelToMs(labels[i]);
  }
  const validMs = labelMs.filter((m) => !Number.isNaN(m));
  let from: string | undefined;
  let to: string | undefined;
  let durationMs: number | undefined;
  let intervalMs: number | undefined;
  if (validMs.length >= 2) {
    const fromMs = validMs[0];
    const toMs = validMs[validMs.length - 1];
    from = new Date(fromMs).toISOString();
    to = new Date(toMs).toISOString();
    durationMs = toMs - fromMs;
    const diffs = validMs
      .slice(1)
      .map((m, i) => m - validMs[i])
      .filter((d) => d > 0)
      .sort((a, b) => a - b);
    if (diffs.length > 0) intervalMs = diffs[Math.floor(diffs.length / 2)];
  } else if (validMs.length === 1) {
    from = new Date(validMs[0]).toISOString();
    to = from;
    durationMs = 0;
  }

  // maxAt / minAt — index 위치의 label 이 시간형이면 ISO 변환
  const indexToIso = (idx: number): string | undefined => {
    if (idx < 0) return undefined;
    const ms = labelMs[idx];
    if (ms == null || Number.isNaN(ms)) return undefined;
    return new Date(ms).toISOString();
  };

  // direction — first→last Δ% 기반
  let direction: SeriesSummary['direction'] = 'unknown';
  if (first !== null && last !== null) {
    if (first === 0 && last === 0) {
      direction = 'flat';
    } else {
      const denom = Math.abs(first) > 1e-9 ? Math.abs(first) : Math.max(Math.abs(last), 1e-9);
      const deltaPct = ((last - first) / denom) * 100;
      if (deltaPct >= 3) direction = 'up';
      else if (deltaPct <= -3) direction = 'down';
      else direction = 'flat';
    }
  }

  return {
    max: count > 0 ? max : null,
    min: count > 0 ? min : null,
    first,
    last,
    avg: count > 0 ? sum / count : null,
    stddev,
    p95,
    pointCount: count,
    nullCount,
    totalCount: count + nullCount,
    direction,
    from,
    to,
    durationMs,
    intervalMs,
    maxAt: indexToIso(maxIdx),
    minAt: indexToIso(minIdx),
  };
};

/** seriesId 패턴 `${dataId}_${index}` 에서 chartDataIndex 추출. 매칭 실패 시 null. */
const extractChartDataIndex = (sid: string): number | null => {
  const m = /^(.+)_(\d+)$/.exec(sid);
  if (!m) return null;
  const idx = Number(m[2]);
  return Number.isFinite(idx) ? idx : null;
};

interface DashboardWidgetCtx {
  /** widget component 의 props.chartData = WidgetChartData[] (각 row 에 dataId 보유). */
  chartData?: Array<Record<string, unknown>>;
  /** widget component 의 setupState.chartRawData = MetricResponse[] (각 row 에 dataDefinition 보유). */
  chartRawData?: Array<Record<string, unknown>>;
}

/**
 * ev-chart element 에서 widget component (TimeSeriesLineWidget / OneDayCompareLineWidget 등)
 * 의 setupState 에 접근해 chartData / chartRawData 회수. Vue 3 internal API 사용.
 *
 * @returns ctx 또는 null (조상 컴포넌트에 chartData/chartRawData 가 없으면)
 */
const getDashboardWidgetCtx = (el: Element): DashboardWidgetCtx | null => {
  try {
    type VueInstance = {
      parent?: VueInstance;
      props?: Record<string, unknown>;
      setupState?: Record<string, unknown>;
    };
    const inst = (el as { __vueParentComponent?: VueInstance }).__vueParentComponent;
    if (!inst) return null;
    let cur: VueInstance | undefined = inst.parent;
    let depth = 0;
    while (cur && depth < 10) {
      const ss = (cur.setupState ?? {}) as Record<string, unknown>;
      const props = (cur.props ?? {}) as Record<string, unknown>;
      const rawCandidate = ss.chartRawData;
      const dataCandidate = ss.chartData ?? props.chartData;
      const unwrap = (v: unknown): unknown => {
        if (v && typeof v === 'object' && 'value' in (v as object)) {
          return (v as { value: unknown }).value;
        }
        return v;
      };
      const rawArr = unwrap(rawCandidate);
      const dataArr = unwrap(dataCandidate);
      if (Array.isArray(rawArr) || Array.isArray(dataArr)) {
        return {
          chartData: Array.isArray(dataArr)
            ? (dataArr as Array<Record<string, unknown>>)
            : undefined,
          chartRawData: Array.isArray(rawArr)
            ? (rawArr as Array<Record<string, unknown>>)
            : undefined,
        };
      }
      cur = cur.parent;
      depth += 1;
    }
    return null;
  } catch {
    return null;
  }
};

/** chartRawData[*].dataDefinition 또는 chartData[*] 에서 metric meta 추출. */
const extractSeriesMeta = (
  ctx: DashboardWidgetCtx | null,
  index: number | null,
  fallbackDataId?: string,
): {
  dataId?: string;
  displayName?: string;
  unit?: string;
  description?: string;
  pcategory?: string;
  ccategory?: string;
} => {
  if (index == null || index < 0) return { dataId: fallbackDataId };
  const cd = ctx?.chartData?.[index] as Record<string, unknown> | undefined;
  const rd = ctx?.chartRawData?.[index] as Record<string, unknown> | undefined;
  const def = (rd?.dataDefinition as Record<string, unknown> | undefined) ?? undefined;
  const dataId =
    (typeof cd?.dataId === 'string' && cd.dataId) ||
    (typeof def?.dataId === 'string' && def.dataId) ||
    fallbackDataId;
  return {
    dataId: typeof dataId === 'string' ? dataId : fallbackDataId,
    displayName: typeof def?.displayName === 'string' ? def.displayName : undefined,
    unit: typeof def?.unit === 'string' ? def.unit : undefined,
    description: typeof def?.description === 'string' ? def.description : undefined,
    pcategory: typeof def?.pcategory === 'string' ? def.pcategory : undefined,
    ccategory: typeof def?.ccategory === 'string' ? def.ccategory : undefined,
  };
};

/**
 * Widget container 에서 title 텍스트 추출.
 * BaseWidgetFrame / .layout-item / [data-widget-id] 안의 헤더에서 찾음.
 */
const pickWidgetTitle = (frame: Element): string | undefined => {
  // 자기 자신 + 직계 자식 헤더 우선
  const candidates = [
    frame.querySelector(
      ':scope > [class*="header"] [class*="title"], ' +
        ':scope > [class*="title"], ' +
        ':scope [class*="widget-frame"] [class*="title"], ' +
        ':scope [class*="widget-frame__header"] [class*="title"], ' +
        // single view rtm-frame: `.rtm-frame-header__title`
        ':scope > .rtm-frame-header > [class*="rtm-frame-header__title"], ' +
        ':scope > [class*="rtm-frame-header"] [class*="title"]',
    ),
    frame.querySelector(':scope > h1, :scope > h2, :scope > h3'),
  ].filter(Boolean) as Element[];
  for (const c of candidates) {
    const t = cleanText(c.textContent);
    if (t) return truncate(t, WIDGET_MAX_TITLE_LEN);
  }
  return undefined;
};

/** Vue 3 internal API — DOM element 의 컴포넌트 props 접근 (canvas widget 데이터용). */
const getVueProps = (el: unknown): Record<string, unknown> | null => {
  try {
    const e = el as {
      __vueParentComponent?: { props?: Record<string, unknown> };
      __vnode?: { component?: { props?: Record<string, unknown> } };
    };
    return e?.__vueParentComponent?.props ?? e?.__vnode?.component?.props ?? null;
  } catch {
    return null;
  }
};

/**
 * BaseWidgetFrame 의 props 메타 (widgetType / chartType / displayStyle / isNoData /
 * chartData[*].dataId) 를 읽어 widget 분류 + dataId 목록을 회수.
 * CSS selector 추측보다 정확한 1차 분류 소스.
 */
interface FrameMeta {
  widgetType?: string; // GRAPHS / LIST / ARCHITECTURE / TEXT 등
  chartType?: string; // TIME_SERIES / TOP_LIST / STATUS_SUMMARY / SERVER / EXCLUSIVE / ...
  displayStyle?: string;
  isNoData?: boolean;
  dataIds?: string[]; // chartData[].dataId — null/중복 제거
}

const readFrameMeta = (frame: Element): FrameMeta => {
  const props = getVueProps(frame) ?? {};
  const widgetType = typeof props.widgetType === 'string' ? props.widgetType : undefined;
  const chartType = typeof props.chartType === 'string' ? props.chartType : undefined;
  const displayStyle = typeof props.displayStyle === 'string' ? props.displayStyle : undefined;
  const isNoData = typeof props.isNoData === 'boolean' ? props.isNoData : undefined;
  // chartData 는 props 또는 ancestor setupState 양쪽에 있을 수 있음
  let chartData: unknown = props.chartData;
  if (!Array.isArray(chartData)) {
    type VueInstance = { parent?: VueInstance; setupState?: Record<string, unknown> };
    const inst = (frame as { __vueParentComponent?: VueInstance }).__vueParentComponent;
    let cur: VueInstance | undefined = inst?.parent;
    let depth = 0;
    while (cur && depth < 4) {
      const ss = (cur.setupState ?? {}) as Record<string, unknown>;
      const v = ss.chartData;
      const unwrapped =
        v && typeof v === 'object' && 'value' in (v as object)
          ? (v as { value: unknown }).value
          : v;
      if (Array.isArray(unwrapped)) {
        chartData = unwrapped;
        break;
      }
      cur = cur.parent;
      depth += 1;
    }
  }
  let dataIds: string[] | undefined;
  if (Array.isArray(chartData)) {
    const ids = (chartData as Array<Record<string, unknown>>)
      .map((c) => (typeof c?.dataId === 'string' ? c.dataId : undefined))
      .filter((s): s is string => !!s);
    if (ids.length > 0) dataIds = Array.from(new Set(ids));
  }
  return { widgetType, chartType, displayStyle, isNoData, dataIds };
};

/**
 * `.base-widget-frame` 에서 BaseWidgetFrame 의 widgetAlertInfo 를 읽어 chatbot context 용
 * alert 객체로 정규화. 우선순위: Vue props → DOM modifier class fallback.
 *
 * - Vue 가능: `__vueParentComponent.props.widgetAlertInfo` 의 isAlertHighlight / alertName /
 *   alertColor / fieldName 인용.
 * - Vue 실패 시: `.base-widget-frame--alert.critical` / `.warning` 클래스 존재 여부로 active 추정.
 */
const extractWidgetAlert = (frame: Element): WidgetSnapshot['alert'] => {
  const props = getVueProps(frame);
  const info = props?.widgetAlertInfo as
    | {
        isAlertHighlight?: boolean;
        showAlertIcon?: boolean;
        alertName?: string;
        alertColor?: string;
        fieldName?: string;
      }
    | undefined;
  if (info && typeof info === 'object') {
    // 활성 판정 — alertName 이 있거나 alertColor 가 비어있지 않거나 showAlertIcon=true 일 때만.
    // isAlertHighlight 자체는 "이 widget 이 alert 표시 기능을 켜둔 상태" 라는 옵션 플래그라
    // 알람 활성 여부 판정에 단독 사용 불가 (false-positive 원인).
    const hasName = typeof info.alertName === 'string' && info.alertName.length > 0;
    const hasColor = typeof info.alertColor === 'string' && info.alertColor.length > 0;
    const showIcon = !!info.showAlertIcon;
    const active = hasName || hasColor || showIcon;
    if (!active) return undefined;
    return {
      active: true,
      level: hasName ? info.alertName : undefined,
      color: hasColor ? info.alertColor : undefined,
      fieldName: typeof info.fieldName === 'string' ? info.fieldName : undefined,
    };
  }
  // DOM fallback — 알람 overlay div 가 렌더된 경우만
  const overlay = frame.querySelector(':scope > .base-widget-frame--alert');
  if (overlay) {
    const cls = overlay.className.toLowerCase();
    let level: string | undefined;
    if (cls.includes('critical')) level = 'critical';
    else if (cls.includes('warning')) level = 'warning';
    return { active: true, level };
  }
  return undefined;
};

/**
 * ECharts widget 의 series 캡처 — `getInstanceByDom().getOption()` 으로 raw option 을
 * 얻은 뒤 raw points 대신 summary stats 로 압축. BaseEChart 는 metric 메타가 없으므로
 * dataDefinition 은 동봉하지 않고 series.name 만 inject.
 */
const captureEChart = (
  el: HTMLElement,
): { series: NonNullable<WidgetSnapshot['series']>; truncated: boolean } | null => {
  try {
    const inst = getInstanceByDom(el as HTMLDivElement);
    if (!inst) return null;
    const opt = inst.getOption() as unknown as {
      series?: Array<Record<string, unknown>>;
      xAxis?: Array<{ data?: unknown[] }> | { data?: unknown[] };
    };
    const rawSeries = Array.isArray(opt.series) ? opt.series : [];
    if (rawSeries.length === 0) return null;
    const xAxisLabels = (() => {
      const ax = opt.xAxis;
      if (Array.isArray(ax)) return Array.isArray(ax[0]?.data) ? ax[0].data : [];
      if (ax && Array.isArray((ax as { data?: unknown[] }).data))
        return (ax as { data: unknown[] }).data;
      return [];
    })();
    const limited = rawSeries.slice(0, WIDGET_MAX_SERIES_PER_CHART);
    const series = limited.map((s) => {
      const rawData = Array.isArray(s.data) ? (s.data as unknown[]) : [];
      // ECharts data 형태: [val, val, ...] 또는 [[ts, val], ...] 또는 [{value: [ts,val]}, ...]
      // 첫 element 형태로 분기해 values + labels 추출
      const values: unknown[] = [];
      const labels: unknown[] = [];
      for (let i = 0; i < rawData.length; i += 1) {
        const d = rawData[i];
        if (Array.isArray(d)) {
          labels.push(d[0]);
          values.push(d[1]);
        } else if (d && typeof d === 'object' && 'value' in (d as object)) {
          const v = (d as { value: unknown }).value;
          if (Array.isArray(v)) {
            labels.push(v[0]);
            values.push(v[1]);
          } else {
            labels.push(xAxisLabels[i]);
            values.push(v);
          }
        } else {
          labels.push(xAxisLabels[i]);
          values.push(d);
        }
      }
      const summary = summarizeNumericSeries(values, labels);
      return {
        name: typeof s.name === 'string' ? s.name : undefined,
        type: typeof s.type === 'string' ? s.type : undefined,
        summary,
      };
    });
    const truncated = rawSeries.length > limited.length;
    return { series, truncated };
  } catch {
    return null;
  }
};

/**
 * EVUI ev-chart widget 의 series 캡처 — Vue 3 internal API 로 props.data 접근 후
 * raw points 대신 summary stats 로 압축. dashboard widget (TimeSeriesLineWidget /
 * OneDayCompareLineWidget 등) 의 ancestor component 의 chartRawData / chartData 에서
 * dataDefinition (displayName/unit/description/category) 도 동봉.
 */
const captureEvChart = (
  el: Element,
): { series: NonNullable<WidgetSnapshot['series']>; truncated: boolean } | null => {
  try {
    const props = getVueProps(el);
    if (!props) return null;
    const dataObj = props.data as
      | {
          series?: Record<string, { name?: string; type?: string }>;
          labels?: unknown[];
          data?: Record<string, unknown[]>;
        }
      | undefined;
    if (!dataObj || typeof dataObj !== 'object') return null;
    const seriesMap = dataObj.series ?? {};
    const seriesIds = Object.keys(seriesMap);
    if (seriesIds.length === 0) return null;
    const labels = Array.isArray(dataObj.labels) ? dataObj.labels : [];
    const dataMap = (dataObj.data ?? {}) as Record<string, unknown[]>;
    const ctx = getDashboardWidgetCtx(el);
    const limitedIds = seriesIds.slice(0, WIDGET_MAX_SERIES_PER_CHART);
    const series = limitedIds.map((sid) => {
      const meta = seriesMap[sid] ?? {};
      const raw = Array.isArray(dataMap[sid]) ? dataMap[sid] : [];
      const summary = summarizeNumericSeries(raw, labels);
      const idx = extractChartDataIndex(sid);
      const fallback = idx != null ? sid.slice(0, sid.length - String(idx).length - 1) : undefined;
      const seriesMeta = extractSeriesMeta(ctx, idx, fallback);
      return {
        name:
          typeof meta.name === 'string' && meta.name ? meta.name : (seriesMeta.displayName ?? sid),
        type: typeof meta.type === 'string' ? meta.type : undefined,
        dataId: seriesMeta.dataId,
        displayName: seriesMeta.displayName,
        unit: seriesMeta.unit,
        description: seriesMeta.description,
        pcategory: seriesMeta.pcategory,
        ccategory: seriesMeta.ccategory,
        summary,
      };
    });
    const truncated = seriesIds.length > limitedIds.length;
    return { series, truncated };
  } catch {
    return null;
  }
};

/** ScoreCardView / StatusSummaryCard / Scoreboard 의 큰 숫자 + 라벨 텍스트 추출. */
const captureScoreValues = (frame: Element): WidgetSnapshot['values'] => {
  const out: NonNullable<WidgetSnapshot['values']> = [];
  // ScoreCardView pattern (큰 숫자 + 좌/우 라벨)
  frame.querySelectorAll('.score-card-view').forEach((card) => {
    const value = cleanText(card.querySelector('.score-card-view__content')?.textContent);
    if (!value) return;
    const labelLeft = cleanText(
      card.querySelector('.score-card-view__title .score-card-view__left')?.textContent,
    );
    const labelRight = cleanText(
      card.querySelector('.score-card-view__title .score-card-view__right')?.textContent,
    );
    const label =
      cleanText(card.querySelector('.score-card-view__title')?.textContent) ||
      [labelLeft, labelRight].filter(Boolean).join(' ');
    out.push({
      label: truncate(label, WIDGET_MAX_LABEL_LEN) || undefined,
      value: truncate(value, WIDGET_MAX_VALUE_LEN),
    });
  });
  // StatusSummaryCard pattern
  frame.querySelectorAll('.status-summary-card').forEach((card) => {
    const value = cleanText(
      card.querySelector('[class*="count"], [class*="value"], [class*="number"]')?.textContent,
    );
    if (!value) return;
    const label = cleanText(
      card.querySelector('[class*="title"], [class*="label"], [class*="name"]')?.textContent,
    );
    out.push({
      label: truncate(label, WIDGET_MAX_LABEL_LEN) || undefined,
      value: truncate(value, WIDGET_MAX_VALUE_LEN),
    });
  });
  // 일반 scoreboard pattern (StatusEvent 등의 [class*="scoreboard"])
  if (out.length === 0) {
    frame.querySelectorAll('[class*="scoreboard"] [class*="value"]').forEach((el) => {
      const value = cleanText(el.textContent);
      if (!value) return;
      // sibling label 추론
      const labelEl =
        el.previousElementSibling ??
        el.parentElement?.querySelector('[class*="label"], [class*="title"]');
      const label = cleanText(labelEl?.textContent ?? '');
      out.push({
        label: truncate(label, WIDGET_MAX_LABEL_LEN) || undefined,
        value: truncate(value, WIDGET_MAX_VALUE_LEN),
      });
    });
  }
  return out.slice(0, WIDGET_MAX_VALUES_PER_WIDGET);
};

/** CircleGaugeChart 의 props.data 접근 (Vue 3 internal API). */
const captureGaugeProps = (wrap: Element): WidgetSnapshot['gauges'] => {
  const props = getVueProps(wrap);
  const arr = (props?.data ?? props?.gaugeData ?? props?.value) as unknown;
  if (!Array.isArray(arr)) return undefined;
  const items = arr.slice(0, WIDGET_MAX_VALUES_PER_WIDGET) as Array<Record<string, unknown>>;
  return items.map((g) => ({
    id: typeof g.id === 'string' ? g.id : typeof g.name === 'string' ? g.name : undefined,
    value: typeof g.value === 'number' ? g.value : null,
    max: typeof g.max === 'number' ? g.max : undefined,
    unit: typeof g.unit === 'string' ? g.unit : undefined,
  }));
};

/**
 * STATUS_SUMMARY widget (Total / Active / InActive 같은 카드형 카운터) — 본 widget 의
 * BEM 클래스가 `.status-summary-card-widget` (substring "card-widget" 포함) 이므로 우리 selector
 * `.status-summary-card` (정확 클래스) 로는 매칭 안 됨. textContent 또는 inner DOM 패턴으로 추출.
 */
const captureStatusSummaryCounts = (frame: Element): WidgetSnapshot['values'] => {
  const out: NonNullable<WidgetSnapshot['values']> = [];
  // 패턴 1: status-summary-card__title-wrapper 내부에 label + count
  const cards = frame.querySelectorAll(
    '.status-summary-card__card-wrapper, [class*="status-summary-card__card"]',
  );
  cards.forEach((card) => {
    const label = cleanText(
      card.querySelector('[class*="title"], [class*="label"], [class*="name"]')?.textContent,
    );
    const value = cleanText(
      card.querySelector('[class*="count"], [class*="value"], [class*="number"]')?.textContent,
    );
    if (value) {
      out.push({
        label: truncate(label, WIDGET_MAX_LABEL_LEN) || undefined,
        value: truncate(value, WIDGET_MAX_VALUE_LEN),
      });
    }
  });
  // Fallback: 자식 텍스트가 "라벨숫자라벨숫자..." 패턴이면 정규표현식으로 split
  if (out.length === 0) {
    const text = cleanText(frame.textContent ?? '');
    const m = text.match(/([A-Za-z가-힣]+)\s*(\d[\d,.]*)/g);
    if (m && m.length > 0) {
      m.slice(0, WIDGET_MAX_VALUES_PER_WIDGET).forEach((seg) => {
        const sm = seg.match(/^([A-Za-z가-힣]+)\s*(\d[\d,.]*)$/);
        if (sm && sm[1] && (sm[1].toLowerCase() !== 'total') === false) {
          out.push({
            label: truncate(sm[1], WIDGET_MAX_LABEL_LEN),
            value: truncate(sm[2], WIDGET_MAX_VALUE_LEN),
          });
        } else if (sm) {
          out.push({
            label: truncate(sm[1], WIDGET_MAX_LABEL_LEN),
            value: truncate(sm[2], WIDGET_MAX_VALUE_LEN),
          });
        }
      });
    }
  }
  return out.slice(0, WIDGET_MAX_VALUES_PER_WIDGET);
};

/**
 * SERVER widget (호스트별 큰 hexagon cube — `.server-node-graph`). 각 노드의 호스트명 +
 * 활성 metric 값을 추출. raw cube 내부 canvas 는 못 읽으므로 legend / label 텍스트 위주.
 */
const captureServerNodes = (frame: Element): WidgetSnapshot['values'] => {
  const out: NonNullable<WidgetSnapshot['values']> = [];
  const nodes = frame.querySelectorAll(
    '.server-node-graph__node, [class*="server-node-graph__node"]',
  );
  nodes.forEach((node) => {
    const text = cleanText(node.textContent);
    if (!text) return;
    out.push({ value: truncate(text, WIDGET_MAX_VALUE_LEN) });
  });
  return out.slice(0, WIDGET_MAX_VALUES_PER_WIDGET);
};

/**
 * EXCLUSIVE widget (Host Overview 같은 hosts × metrics 비율 표). 텍스트로만 캡처 (DOM rich
 * inner structure 직접 파싱 비용 대비 ROI 낮음).
 */
const captureOverviewText = (frame: Element): WidgetSnapshot['values'] => {
  const out: NonNullable<WidgetSnapshot['values']> = [];
  const rows = frame.querySelectorAll(
    '.exclusive-style-1-chart > *, [class*="exclusive-style"] [class*="row"], [class*="metric-selection-area"] + *',
  );
  rows.forEach((row) => {
    const text = cleanText(row.textContent);
    if (!text) return;
    out.push({ value: truncate(text, WIDGET_MAX_VALUE_LEN) });
  });
  // fallback: textContent split by lines
  if (out.length === 0) {
    const text = cleanText(frame.textContent);
    if (text) out.push({ value: truncate(text, WIDGET_MAX_VALUE_LEN * 3) });
  }
  return out.slice(0, WIDGET_MAX_VALUES_PER_WIDGET);
};

/** HexagonChart 의 props.hexagonData 접근 (Vue 3 internal API). */
const captureHexagonProps = (wrap: Element): WidgetSnapshot['hexagons'] => {
  const props = getVueProps(wrap);
  const arr = (props?.hexagonData ?? props?.data) as unknown;
  if (!Array.isArray(arr)) return undefined;
  const items = arr.slice(0, WIDGET_MAX_HEXAGON_CELLS) as Array<Record<string, unknown>>;
  return items.map((h) => ({
    key:
      typeof h.key === 'string'
        ? h.key
        : typeof h.name === 'string'
          ? h.name
          : typeof h.id === 'string'
            ? h.id
            : undefined,
    value: typeof h.value === 'number' ? h.value : null,
    level:
      typeof h.level === 'string' ? h.level : typeof h.status === 'string' ? h.status : undefined,
  }));
};

/**
 * Dashboard widget container 들을 순회해 kind 별 raw 데이터 캡처.
 * `.base-widget-frame` 또는 `.layout-item` 또는 `[data-widget-id]` 가 있는 화면에서만 동작.
 */
const collectWidgetSnapshots = (): WidgetSnapshot[] | undefined => {
  // Dashboard 와 single view / multi view 양쪽의 widget container 를 동시 매칭.
  // - `.base-widget-frame` / `.layout-item` / `[data-widget-id]` : dashboard widgets (BaseWidgetFrame)
  // - `.rtm-frame:not(.rtm-frame--type-group)` : 공용 BaseFrame 컴포넌트 (`rtm-frame`).
  //   PostgreSQL/MySQL singleView 는 `.rtm-frame--type-detail` modifier 사용,
  //   Oracle/SQL Server/Altibase/Tibero 등은 modifier 없이 plain `rtm-frame` 사용.
  //   `--type-group` 은 outer 섹션 wrapper 라 명시 제외 (그 자식 leaf frame 만 캡처).
  const dashSelector = '.base-widget-frame, .layout-item, [data-widget-id], [id^="widget-"]';
  const frameSelector = '.rtm-frame:not(.rtm-frame--type-group)';
  const allFrames = Array.from(
    document.querySelectorAll(`${dashSelector}, ${frameSelector}`),
  ) as Element[];
  // nested rtm-frame 중복 제거 — leaf 만 보존 (자기 안에 다른 매칭 frame 이 없는 element).
  // `f.contains(other)` (= f 가 다른 매칭 frame 의 조상) 이면 outer wrapper → 제외.
  const frames = allFrames.filter(
    (f, _idx, arr) => !arr.some((other) => other !== f && f.contains(other)),
  );
  if (frames.length === 0) return undefined;

  const out: WidgetSnapshot[] = [];
  for (const frame of frames.slice(0, WIDGET_MAX_COUNT)) {
    if (isInExcludedArea(frame)) continue;
    try {
      const widget_id = frame.getAttribute('data-widget-id') ?? frame.id ?? undefined;
      const title = pickWidgetTitle(frame);
      const alert = extractWidgetAlert(frame);
      const meta = readFrameMeta(frame);
      const base = {
        widget_id,
        title,
        alert,
        chartType: meta.chartType,
        dataIds: meta.dataIds,
        noData: meta.isNoData,
      };

      // 0) BaseWidgetFrame 의 chartType 으로 1차 분류 — CSS selector 보다 정확
      const ct = (meta.chartType ?? '').toUpperCase();

      // STATUS_SUMMARY (Total/Active/InActive 카드 등)
      if (ct === 'STATUS_SUMMARY') {
        const values = captureStatusSummaryCounts(frame);
        out.push({
          ...base,
          kind: 'score',
          analyzable: !!values && values.length > 0,
          values,
          hint:
            !values || values.length === 0 ? 'STATUS_SUMMARY values not extractable' : undefined,
        });
        continue;
      }
      // SERVER (호스트 hexagon cube 그래픽 — `.server-node-graph`)
      if (ct === 'SERVER') {
        const values = captureServerNodes(frame);
        out.push({
          ...base,
          kind: 'server',
          analyzable: !!values && values.length > 0,
          values,
          hint:
            !values || values.length === 0
              ? 'SERVER widget — canvas-based, only legend text captured'
              : undefined,
        });
        continue;
      }
      // EXCLUSIVE (Host Overview 같은 다중 metric 표)
      if (ct === 'EXCLUSIVE') {
        const values = captureOverviewText(frame);
        out.push({
          ...base,
          kind: 'overview',
          analyzable: !!values && values.length > 0,
          values,
          hint:
            !values || values.length === 0
              ? 'EXCLUSIVE widget — text scraping fell back'
              : undefined,
        });
        continue;
      }
      // TOP_LIST (CPU 사용량 TOP 10 같은 list 형식)
      if (ct === 'TOP_LIST') {
        if (meta.isNoData) {
          out.push({
            ...base,
            kind: 'top_list',
            analyzable: false,
            hint: 'no data available',
          });
          continue;
        }
        // 데이터 있을 때 — list rows 텍스트로 추출
        const values: NonNullable<WidgetSnapshot['values']> = [];
        frame
          .querySelectorAll('[class*="top-list"] [class*="row"], [class*="ranking"] [class*="row"]')
          .forEach((row) => {
            const text = cleanText(row.textContent);
            if (text) values.push({ value: truncate(text, WIDGET_MAX_VALUE_LEN) });
          });
        out.push({
          ...base,
          kind: 'top_list',
          analyzable: values.length > 0,
          values: values.slice(0, WIDGET_MAX_VALUES_PER_WIDGET),
          hint: values.length === 0 ? 'TOP_LIST rows not extractable' : undefined,
        });
        continue;
      }
      // TEXT (대시보드 제목 / 메모)
      if (ct === 'TEXT') {
        out.push({
          ...base,
          kind: 'text',
          analyzable: false,
          hint: 'text-only widget (title/memo)',
        });
        continue;
      }

      // TIME_SERIES / ONE_DAY_COMPARE / BAR_CHART / 기타 차트 — ECharts / EvChart 추출
      // 1a) ECharts (BaseEChart) — getInstanceByDom 으로 raw option 추출
      const echartEl = frame.querySelector('.base-e-chart, .ec-chart') as HTMLElement | null;
      if (echartEl) {
        const snap = captureEChart(echartEl);
        if (snap && snap.series.length > 0) {
          out.push({
            ...base,
            kind: 'echart',
            analyzable: true,
            series: snap.series,
            truncated: snap.truncated,
          });
          continue;
        }
      }

      // 1b) EVUI ev-chart (dashboard widgets — TimeSeriesLineWidget 등) — Vue props.data 접근
      const evChartEl = frame.querySelector('.ev-chart');
      if (evChartEl) {
        const snap = captureEvChart(evChartEl);
        if (snap && snap.series.length > 0) {
          out.push({
            ...base,
            kind: 'echart',
            analyzable: true,
            series: snap.series,
            truncated: snap.truncated,
          });
          continue;
        }
      }

      // TIME_SERIES / ONE_DAY_COMPARE / BAR_CHART 등 chart 류 + isNoData=true 또는 ev-chart 비매칭
      // → ev-chart 미렌더. dataIds 만 inject.
      const isChartLike =
        ct === 'TIME_SERIES' ||
        ct === 'ONE_DAY_COMPARE' ||
        ct === 'BAR_CHART' ||
        ct === 'LINE_CHART' ||
        ct === 'AREA_CHART';
      if (isChartLike) {
        out.push({
          ...base,
          kind: 'echart',
          analyzable: false,
          hint: meta.isNoData
            ? 'no data available'
            : `${ct} widget — chart not rendered or capture failed`,
        });
        continue;
      }

      // 2) 일반 score (CSS selector 매칭 — STATUS_SUMMARY 외 score 형 widget)
      const hasScoreEl =
        frame.querySelector('.score-card-view, .status-summary-card, [class*="scoreboard"]') !==
        null;
      if (hasScoreEl) {
        const values = captureScoreValues(frame);
        if (values && values.length > 0) {
          out.push({
            ...base,
            kind: 'score',
            analyzable: true,
            values,
          });
          continue;
        }
      }

      // 3) Gauge (CircleGaugeChart) — Canvas wrapper
      const gaugeWrap = frame.querySelector(
        '.canvasWrapper, [class*="gauge-chart"], [class*="circle-gauge"]',
      );
      if (gaugeWrap) {
        const gauges = captureGaugeProps(gaugeWrap);
        if (gauges && gauges.length > 0) {
          out.push({
            ...base,
            kind: 'gauge',
            analyzable: true,
            gauges,
          });
          continue;
        }
        out.push({
          ...base,
          kind: 'gauge',
          analyzable: false,
          hint: 'Canvas-rendered gauge — props ref unavailable',
        });
        continue;
      }

      // 4) Hexagon
      const hexWrap = frame.querySelector('.hexagon-chart, [class*="hexagon"]');
      if (hexWrap) {
        const hexagons = captureHexagonProps(hexWrap);
        if (hexagons && hexagons.length > 0) {
          out.push({
            ...base,
            kind: 'hexagon',
            analyzable: true,
            hexagons,
          });
          continue;
        }
        out.push({
          ...base,
          kind: 'hexagon',
          analyzable: false,
          hint: 'Canvas-rendered hexagon — props ref unavailable',
        });
        continue;
      }

      // 5) 미분류 — title / chartType / alert / dataIds 중 하나라도 있으면 inject (LLM context 유지)
      if (title || alert || meta.chartType || (meta.dataIds && meta.dataIds.length > 0)) {
        out.push({
          ...base,
          kind: 'unknown',
          analyzable: false,
          hint: meta.isNoData ? 'no data available' : 'graphical widget — see screen for details',
        });
      }
    } catch (e) {
      // 한 widget 실패가 전체 캡처를 막지 않도록 skip
      console.warn('[chatbot] widget capture failed:', e);
    }
  }
  return out.length > 0 ? out : undefined;
};

/**
 * dataSnapshot 의 JSON.stringify size 가 SNAPSHOT_MAX_BYTES 를 초과하면 widgets 마지막부터
 * drop 하여 cap 안으로. tables / filters 는 우선순위 보존.
 */
const enforceSnapshotSizeCap = (snap: DataSnapshot): DataSnapshot => {
  if (!snap.widgets || snap.widgets.length === 0) return snap;
  const widgets = [...snap.widgets];
  let serialized = '';
  while (widgets.length > 0) {
    serialized = JSON.stringify({ ...snap, widgets });
    if (serialized.length <= SNAPSHOT_MAX_BYTES) break;
    widgets.pop();
  }
  if (widgets.length === 0 && serialized.length > SNAPSHOT_MAX_BYTES) {
    // widgets 다 빼도 cap 초과 — tables / filters 자체로 cap 초과인 경우. 그대로 반환.
    return { ...snap, widgets: undefined };
  }
  return { ...snap, widgets: widgets.length === snap.widgets.length ? snap.widgets : widgets };
};

const collectDataSnapshot = (): DataSnapshot | undefined => {
  try {
    const filters = collectFilters();
    const tables = collectTables();
    const widgets = collectWidgetSnapshots();
    if (
      (!filters || filters.length === 0) &&
      (!tables || tables.length === 0) &&
      (!widgets || widgets.length === 0)
    ) {
      return undefined;
    }
    const snap: DataSnapshot = {
      filters: filters.length > 0 ? filters : undefined,
      tables,
      widgets,
    };
    const finalSnap = enforceSnapshotSizeCap(snap);
    // 디버깅: widget 캡처 결과 요약 (문제 진단용 — 추후 제거 가능)
    if (typeof console !== 'undefined') {
      const widgetList = finalSnap.widgets ?? [];
      const kindCounts = widgetList.reduce<Record<string, number>>((acc, w) => {
        acc[w.kind] = (acc[w.kind] ?? 0) + 1;
        return acc;
      }, {});
      const alertedWidgets = widgetList.filter((w) => w.alert?.active);
      console.log('[chatbot] dataSnapshot:', {
        filters: finalSnap.filters?.length ?? 0,
        tables: finalSnap.tables?.length ?? 0,
        widgets: widgetList.length,
        widgetKinds: kindCounts,
        alertedCount: alertedWidgets.length,
        alertedTitles: alertedWidgets.map((w) => `${w.title ?? '?'}(${w.alert?.level ?? '?'})`),
        sizeBytes: JSON.stringify(finalSnap).length,
      });
    }
    return finalSnap;
  } catch (e) {
    // DOM 변형으로 인한 실패는 silently 무시 — pageContext 본 흐름 깨지지 않게

    console.warn('[chatbot] collectDataSnapshot failed:', e);
    return undefined;
  }
};

/**
 * GNB / 좌측 메뉴 / 챗봇 자체 등 contents 와 무관한 영역인지 판정.
 * 이 영역 내부 텍스트는 fragments 에서 제외.
 */
const isInExcludedArea = (el: Element): boolean => {
  // 가장 가까운 차단 영역 찾기 — 매칭되면 제외.
  return !!el.closest(
    '.base-menu-wrapper, .base-menu, .gnb, .global-nav-bar, ' +
      '.chat-window__wrapper, .chat-window, ' +
      '.notice-window, .api-trace-window',
  );
};

export const collectPageContext = (): PageContext => {
  // query/hash 는 수집하지 않는다 — 검색어·필터값 등 사용자 입력이 URL 파라미터로 실려
  // LLM 컨텍스트에 노출될 수 있고, 화면 전환 감지(path 비교)에는 pathname 으로 충분 (리뷰 P1-3)
  const url = window.location.origin + window.location.pathname;
  const path = window.location.pathname;
  let title = cleanText(document.title);

  const fragments: string[] = [];
  const seen = new Set<string>();

  const push = (raw: string | null | undefined) => {
    const txt = cleanText(raw);
    if (!txt) return;
    if (txt.length > MAX_FRAGMENT_LEN) return;
    if (seen.has(txt)) return;
    seen.add(txt);
    fragments.push(txt);
  };

  // 1) navigation-header 의 view name + breadcrumb path — 가장 신뢰도 높은 페이지 식별자
  document.querySelectorAll('.navigation-header__name, .view-name').forEach((el) => {
    if (isInExcludedArea(el)) return;
    push(el.textContent);
  });
  document
    .querySelectorAll('.navigation-header__path, .navigation-header__path > *')
    .forEach((el) => {
      if (isInExcludedArea(el)) return;
      push(el.textContent);
    });

  // navigation-header__name 이 있으면 title 도 그것으로 대체 (document.title 은 항상 'exemONE')
  if (title === 'exemONE' || title === '') {
    const nameEl = document.querySelector('.navigation-header__name, .view-name');
    if (nameEl && !isInExcludedArea(nameEl)) {
      const nameTxt = cleanText(nameEl.textContent);
      if (nameTxt) title = nameTxt;
    }
  }

  // 2) 메인 콘텐츠 헤딩 — GNB / 챗봇 / 노티스 영역 제외
  document.querySelectorAll('h1, h2, h3').forEach((el) => {
    if (isInExcludedArea(el)) return;
    push(el.textContent);
  });

  // 3) exemONE 패턴: section/card/PaBox 헤더들
  document
    .querySelectorAll(
      '[class*="__section__title"], [class*="card__title"], [class*="page-title"], ' +
        '.pa-title, .pa-box__title',
    )
    .forEach((el) => {
      if (isInExcludedArea(el)) return;
      push(el.textContent);
    });

  // 4) 현재 화면의 alarm 메시지 카드 (Current Alert / 알람 요약 등)
  document
    .querySelectorAll('[class*="alert"][class*="title"], [class*="alarm-card"]')
    .forEach((el) => {
      if (isInExcludedArea(el)) return;
      push(el.textContent);
    });

  // truncate
  const finalFragments = fragments.slice(0, MAX_FRAGMENTS);

  // 화면이 등록한 엔티티 값도 백엔드/LLM 으로 나가는 경로 — PII 패턴 마스킹 적용 (재리뷰 P1)
  const activeEntity =
    typeof window !== 'undefined' && window.__chatbotActiveEntity
      ? Object.fromEntries(
          Object.entries(window.__chatbotActiveEntity).map(([k, v]) => [k, redactPii(String(v))]),
        )
      : undefined;

  const dataSnapshot = collectDataSnapshot();

  return {
    url,
    path,
    title,
    fragments: finalFragments,
    capturedAt: new Date().toISOString(),
    activeEntity,
    dataSnapshot,
  };
};

/** Backend ChatRequest 와 같은 형태의 pageContext 부분만 직렬화. */
export const serializePageContext = (ctx: PageContext): Record<string, unknown> => ({
  url: ctx.url,
  path: ctx.path,
  title: ctx.title,
  fragments: ctx.fragments,
  capturedAt: ctx.capturedAt,
  activeEntity: ctx.activeEntity,
  dataSnapshot: ctx.dataSnapshot,
  previousPath: ctx.previousPath,
});
